<template>
  <section class="section is-medium testimonials-section">
    <div class="container">
      <div class="has-text-centered mb-6">
        <h2 class="title is-2 has-line animate fade-in-up">
          {{ $t('landing.testimonials-title') }}
        </h2>
      </div>
      
      <div class="columns is-multiline">
        <div
          v-for="(testimonial, index) in testimonials"
          :key="index"
          class="column is-4"
        >
          <div
            class="testimonial-card animate fade-in-up"
            :class="`animate--delay-${index}`"
          >
            <div class="testimonial-quote">
              <i class="fas fa-quote-left quote-icon" />
              <p class="testimonial-text">
                {{ $t(testimonial.quote) }}
              </p>
            </div>
            <div class="testimonial-author">
              <div
                class="author-avatar"
                :style="`background-color: ${testimonial.color}`"
              >
                {{ getInitials(testimonial.author) }}
              </div>
              <div class="author-info">
                <h4 class="author-name">
                  {{ $t(testimonial.author) }}
                </h4>
                <p class="author-title">
                  {{ $t(testimonial.title) }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { useI18n } from 'vue-i18n';

// Initialize i18n
const { t } = useI18n();

const testimonials = [
  {
    quote: 'landing.testimonial1-quote',
    author: 'landing.testimonial1-author',
    title: 'landing.testimonial1-title',
    color: '#4A7FEC', // indiefy-blue
  },
  {
    quote: 'landing.testimonial2-quote',
    author: 'landing.testimonial2-author',
    title: 'landing.testimonial2-title',
    color: '#25BDB0', // indiefy-teal
  },
  {
    quote: 'landing.testimonial3-quote',
    author: 'landing.testimonial3-author',
    title: 'landing.testimonial3-title',
    color: '#FB6CBC', // indiefy-pink
  },
];

// Helper function to get initials from name
const getInitials = (nameKey) => {
  // Remove the 'landing.' prefix to get just the key part
  const key = nameKey.replace('landing.', '');
  
  // Extract words after last dash in testimonial1-author format
  const namePart = key.split('-').pop();
  
  // For names stored as "first-name"
  if (namePart === 'author') {
    // Use translation key for default initials
    return t('landing.default-initials');
  }
  
  return t('landing.default-initials'); // Default fallback using translation
};
</script>

<style scoped lang="scss">
@import "@/styles/abstracts/colors";
@import "@/styles/abstracts/typography";
@import "@/styles/abstracts/animations";

.testimonials-section {
  background-color: var(--color-background-alt, #f8f9fa);
}

.testimonial-card {
  background-color: var(--color-background, white);
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15);
  }
}

.testimonial-quote {
  position: relative;
  margin-bottom: 2rem;
  flex-grow: 1;
}

.quote-icon {
  font-size: 2rem;
  color: rgba(0, 0, 0, 0.1);
  position: absolute;
  top: -10px;
  left: -5px;
  
  .body--dark & {
    color: rgba(255, 255, 255, 0.1);
  }
}

.testimonial-text {
  color: var(--color-text, #333);
  font-style: italic;
  font-size: 1.05rem;
  line-height: 1.6;
  position: relative;
  padding-left: 1rem;
}

.testimonial-author {
  display: flex;
  align-items: center;
}

.author-avatar {
  width: 50px;
  height: 50px;
  border-radius: 25px;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 1.2rem;
  margin-right: 1rem;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.author-info {
  display: flex;
  flex-direction: column;
}

.author-name {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--color-text, #333);
  margin-bottom: 0.3rem;
}

.author-title {
  font-size: 0.9rem;
  color: var(--color-text-light, #666);
}

@media screen and (max-width: 768px) {
  .testimonial-card {
    margin-bottom: 2rem;
  }
}
</style>